import React, { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import Auth from '@Services/Auth';
import AppStorage from '@Services/AppStorage';
import PropTypes from 'prop-types';
import { Modal } from '@els/els-react--modal';
import { ButtonWithIcon } from '@els/els-react--button';
import { historyReducerKey } from '../Constants';
import { handleNameChange, onSubmitForm, closeSaveModal } from '../HistoryAction';
import ConfirmationPopup from '../ConfirmationPopup';
import { trackAdobeAnalyticsError, trackConversionDriverClick } from '../analytics';

import './SaveModal.scss';

function SaveModal({ closed, selfClose }) {
  const {
    itemToBeSavedContext,
    saveModalTitle,
    saveModalNameInput,
    isInputTouched,
    itemToBeSaved,
    isSaveInProgress,
    error,
    showSaveModal,
  } = useSelector((state) => state.get(historyReducerKey));
  const [isTextBoxFocused, setTextBoxFocus] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const dispatch = useDispatch();
  const inputRef = useRef();
  const trimmedName = saveModalNameInput.trim();
  const errorTitle = 'Login required';
  const errorMessage = 'Please sign-in/register to save your History.';
  useEffect(() => {
    if (inputRef && inputRef.current && showSaveModal) {
      inputRef.current.focus();
      setTextBoxFocus(true);
    }
  }, [showSaveModal]);

  useEffect(() => {
    if (!showSaveModal) {
      setIsModalOpen(false);
    }
  }, [showSaveModal]);

  useEffect(() => {
    if (closed) {
      setIsModalOpen(false);
      dispatch(closeSaveModal());
    }
  }, [closed]);

  const isTextInputError = () =>
    (isInputTouched && trimmedName === '') ||
    (trimmedName.length < 3 && trimmedName.length > 0) ||
    trimmedName.length > 50 ||
    (error && error.message);
  const isSaveDisabled = () => isSaveInProgress || trimmedName === '' || trimmedName.length < 3 || trimmedName.length > 50;
  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(onSubmitForm(itemToBeSaved, trimmedName, saveModalTitle === 'Rename'));
  };
  const resolveFormError = () => {
    if (!trimmedName) {
      return '';
    }
    if (!trimmedName) {
      return 'Name is required.';
    }
    if (trimmedName.length < 3) {
      return 'Name is too short, minimum length is 3 characters.';
    }
    if (trimmedName.length > 50) {
      return 'Name is too long, maximum length is 50 characters.';
    }
    return '';
  };

  const trackError = () => {
    const formError = resolveFormError();
    trackAdobeAnalyticsError(formError);
  };

  const signIn = () => {
    history.push({
      pathname: generatePath(paths.LOGIN),
    });
  };

  const handleConirmSignIn = (handleSignIn) => {
    dispatch(closeSaveModal());
    handleSignIn();
  };

  if (Auth.isAnonymousUser()) {
    return (
      <ConfirmationPopup
        title={errorTitle}
        text={errorMessage}
        submitButtonText="Sign in"
        dismissButtonText="Cancel"
        isCancelButton
        onClose={() => {
          dispatch(closeSaveModal());
        }}
        onConfirm={() => {
          trackConversionDriverClick('history:savesearch:sign-in');
          AppStorage.instance.set('scheduledHistory', itemToBeSaved);
          handleConirmSignIn(signIn);
        }}
        closed={closed}
        selfClose={selfClose}
      />
    );
  }
  return (
    isModalOpen && (
      <Modal
        style={{ minHeight: '20rem' }}
        className="rx-history-modal"
        shouldHideCloseButton
        onClose={() => {
          setIsModalOpen(false);
          dispatch(closeSaveModal());
        }}>
        <header className="rx-modal-content-header">
          <h4 className="rx-modal-title">{`${saveModalTitle} ${itemToBeSavedContext.replace(/"/g, "'")}`}</h4>
          <ButtonWithIcon
            type="link"
            iconOnly
            sprite="Close"
            onClick={() => {
              setIsModalOpen(false);
              dispatch(closeSaveModal());
            }}>
            Cancel
          </ButtonWithIcon>
        </header>
        <form onSubmit={handleSubmit}>
          <div className="rx-modal-content-main">
            {isSaveInProgress && <RxSpinnerComponent autoStart spinnerScale={1} spinnerId="history-save-loading" />}
            <section className="rx-modal-history-content">
              <div className="history-form-label">Name of result set:</div>
              <div className="history-form-content">
                <div className={`els-text-input small ${isTextInputError() ? 'els-text-input-error' : ''}`}>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className={`els-text-input-label ${isTextBoxFocused || trimmedName.length > 0 ? 'active' : ''}`}>
                    Enter a name
                  </label>
                  <input
                    id="searchName"
                    className={`els-text-input-field ${isTextBoxFocused || trimmedName.length > 0 ? 'active' : ''}`}
                    ref={inputRef}
                    key="searchNameKey"
                    onBlur={() => {
                      setTextBoxFocus(false);
                      trackError();
                    }}
                    onFocus={() => {
                      setTextBoxFocus(true);
                    }}
                    type="text"
                    name="searchName"
                    autoComplete="off"
                    required
                    value={saveModalNameInput || ''}
                    onChange={(e) => dispatch(handleNameChange(e.target.value))}
                  />
                  {error && error.message && <span className="els-text-input-label-error">Error saving this search</span>}
                  {trimmedName.length < 3 && trimmedName.length > 0 && (
                    <span className="els-text-input-label-error">The name is too short, minimum length is 3 characters.</span>
                  )}
                  {trimmedName.length > 50 && (
                    <span className="els-text-input-label-error">The name is too long, maximum length is 50 characters.</span>
                  )}
                  {isInputTouched && trimmedName === '' && <span className="els-text-input-label-error">The name is required.</span>}
                </div>
              </div>
            </section>
          </div>
          <footer className="rx-modal-content-footer">
            <span className="rx-btn-group--right history-btn-group">
              <ButtonWithIcon
                className="e2e-save-modal-cancel u-els-margin-right-1o2"
                type="secondary"
                iconRight
                sprite="Close"
                onClick={() => {
                  setIsModalOpen(false);
                  dispatch(closeSaveModal());
                }}>
                Cancel
              </ButtonWithIcon>
              <ButtonWithIcon
                className="e2e-save-modal-save"
                sprite="ChevronRight"
                iconRight
                htmlType="submit"
                disabled={isSaveDisabled()}
                onClick={(event) => {
                  handleSubmit(event);
                }}>
                Save
              </ButtonWithIcon>
            </span>
          </footer>
        </form>
      </Modal>
    )
  );
}

SaveModal.propTypes = {
  selfClose: PropTypes.func,
  closed: PropTypes.bool,
};

export default SaveModal;
